/**
 * Employees Page - Microsoft 365 Style with Fluent UI DataGrid
 * Professional employee management with M365 design patterns
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Text,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  MoreHorizontalRegular,
  SearchRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../components/layout';
import { TablePagination } from '../../components/common';
import { usePagination } from '../../hooks/usePagination';
import {
  ApiClient,
  newToast,
  fullDate,
  getEmployeeImageUrl,
} from '../../configurations/index.config';
import { getErrorMessage } from '../../types';
import { useAppContext } from '../../contexts/AppContainer';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  employeeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButton: {
    minWidth: 'auto',
    padding: '6px',
  },
  badge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
  activeBadge: {
    backgroundColor: '#d1f0e7',
    color: '#107c10',
  },
  inactiveBadge: {
    backgroundColor: '#f3f2f1',
    color: '#605e5c',
  },
});

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  other_names?: string;
  email: string;
  phone: string;
  date_of_birth?: string;
  role_name?: string;
  department_name?: string;
  status?: string;
  face_url?: string;
}

export const EmployeesFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { profileData } = useAppContext() as any;
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (profileData?.userId) {
      fetchEmployees();
    }
  }, [profileData?.userId]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        const employeeData = response.data.data || [];
        // Deduplicate employees by id
        const uniqueEmployees = employeeData.filter(
          (emp: Employee, index: number, self: Employee[]) =>
            index === self.findIndex(e => e.id === emp.id)
        );
        setEmployees(uniqueEmployees);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch employees');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleAddEmployee = () => {
    navigate('/account/add-employee');
  };

  const handleViewEmployee = (employee: Employee) => {
    navigate(`/account/employee-details/${employee.id}`, { state: { employee } });
  };

  const handleEditEmployee = (employee: Employee) => {
    // Navigate to edit page or open edit dialog
    navigate(`/account/add-employee`, { state: { employee } });
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-employee', {
        employee_id: selectedEmployee.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Employee deactivated successfully');
        fetchEmployees();
      } else {
        newToast('error', response.data?.message || 'Failed to deactivate employee');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleExport = () => {
    // Export to CSV
    const csvData = employees.map(emp => ({
      'First Name': emp.first_name,
      'Last Name': emp.last_name,
      Email: emp.email,
      Phone: emp.phone,
      Role: emp.role_name || 'N/A',
      Department: emp.department_name || 'N/A',
    }));

    // Check if there's data to export
    if (csvData.length === 0) {
      newToast('error', 'No employees to export');
      return;
    }

    // TypeScript-safe: we know csvData[0] exists due to length check above
    const headers = Object.keys(csvData[0]!).join(',');
    const rows = csvData.map(row => Object.values(row).join(','));
    const csvContent = [headers, ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredEmployees = employees.filter(emp => {
    const query = searchQuery.toLowerCase();
    return (
      emp.first_name?.toLowerCase().includes(query) ||
      emp.last_name?.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query) ||
      emp.phone?.includes(query) ||
      emp.role_name?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredEmployees,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Employees</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${employees.length} total employees`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={handleAddEmployee} onExport={handleExport} addLabel="Add Employee" />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search employees..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Employees Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading employees...</Text>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold">
              No employees found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery ? 'Try adjusting your search' : 'Click "Add Employee" to get started'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Employee</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Phone</th>
                <th className={styles.th}>Role</th>
                <th className={styles.th}>Department</th>
                <th className={styles.th}>Date of Birth</th>
                <th className={styles.th} style={{ width: '80px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(employee => (
                <tr key={employee.id} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.employeeCell}>
                      <Avatar
                        name={`${employee.first_name} ${employee.last_name}`}
                        image={
                          employee.face_url
                            ? { src: getEmployeeImageUrl(employee.face_url) }
                            : undefined
                        }
                        color="brand"
                        size={32}
                      />
                      <div>
                        <Text weight="semibold" style={{ display: 'block' }}>
                          {employee.first_name} {employee.last_name}
                        </Text>
                        {employee.other_names && (
                          <Text size={200} style={{ color: '#605e5c' }}>
                            {employee.other_names}
                          </Text>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={styles.td}>{employee.email}</td>
                  <td className={styles.td}>{employee.phone}</td>
                  <td className={styles.td}>{employee.role_name || '—'}</td>
                  <td className={styles.td}>{employee.department_name || '—'}</td>
                  <td className={styles.td}>
                    {employee.date_of_birth ? fullDate(employee.date_of_birth) : '—'}
                  </td>
                  <td className={styles.td}>
                    <Menu>
                      <MenuTrigger>
                        <Button
                          icon={<MoreHorizontalRegular />}
                          appearance="subtle"
                          className={styles.actionButton}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem
                            icon={<EyeRegular />}
                            onClick={() => handleViewEmployee(employee)}
                          >
                            View Details
                          </MenuItem>
                          <MenuItem
                            icon={<EditRegular />}
                            onClick={() => handleEditEmployee(employee)}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={<DeleteRegular />}
                            onClick={() => {
                              setSelectedEmployee(employee);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredEmployees.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Deactivate Employee</DialogTitle>
            <DialogContent>
              Are you sure you want to deactivate{' '}
              <strong>
                {selectedEmployee?.first_name} {selectedEmployee?.last_name}
              </strong>
              ? The employee will be set to inactive status.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteEmployee}>
                Deactivate
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default EmployeesFluentUI;
